import { AngleLeftIcon } from '@app/components/icons/angle-left';
import { CloseNewIcon } from '@app/components/icons/close-new';
import {
  selectHasPublishToEventsPendingAreaResolution,
  selectIsPublishToEventsDirty,
  selectPublishToEventsDraftSelectedEvents,
  selectPublishToEventsSearchText,
  selectPublishToEventsStatus,
} from '@app/routes/content/selectors';
import { getPathParams } from '@app/routes/utils';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React from 'react';

import { selectRecording } from '../../selector';
import { EventListContainer } from './event-list';
import { SearchBox } from './search-box';

type TPublishToEventsProps = {
  onClose: () => void;
  onBack: () => void;
};

export const PublishToEvents = (props: TPublishToEventsProps) => {
  const { onClose, onBack } = props;
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const { recordingId: recordingIdFromPath } = getPathParams(['recordingId']);
  const searchText = useAppSelector(selectPublishToEventsSearchText);
  const status = useAppSelector(selectPublishToEventsStatus);
  const isDirty = useAppSelector(selectIsPublishToEventsDirty);
  const selectedEvents = useAppSelector(
    selectPublishToEventsDraftSelectedEvents,
  );
  const hasPendingAreaResolution = useAppSelector(
    selectHasPublishToEventsPendingAreaResolution,
  );
  const selectedEventsCount = Object.keys(selectedEvents).length;
  const selectedReplayAreasCount =
    Object.values(selectedEvents).filter(Boolean).length;
  const isSearching = status === 'processing' && Boolean(searchText.trim());
  const recordingId = recording?.id ?? recordingIdFromPath;

  return (
    <div className="w-[29rem] [&>*]:border-t [&>*]:border-white/10 [&>*:first-child]:border-t-0">
      <Header onClose={onClose} onBack={onBack} />
      <SearchBox
        value={searchText}
        isSearching={isSearching}
        onChange={e =>
          dispatch({
            type: '[content] publish-to-events search text changed',
            payload: { searchText: e.target.value },
          })
        }
        onClear={() =>
          dispatch({
            type: '[content] publish-to-events search text changed',
            payload: { searchText: '' },
          })
        }
      />
      <EventListContainer />
      <Footer
        disabled={!isDirty || hasPendingAreaResolution}
        selectedEventsCount={selectedEventsCount}
        selectedReplayAreasCount={selectedReplayAreasCount}
        onPublish={() => {
          if (!recordingId) return;

          dispatch({
            type: '[content] publish-to-events save started',
            payload: {
              recordingId,
            },
          });
        }}
      />
    </div>
  );
};

const Header = ({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <header className="flex items-center px-3 py-2.5">
      <button
        onClick={onBack}
        className={cx(
          'p-2 hover:bg-gray-700 rounded-lg',
          'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
        )}
      >
        <span className="sr-only">{t('common.back')}</span>
        <AngleLeftIcon className="w-5 h-5" />
      </button>

      <div className="ml-2 flex min-w-0 flex-1 items-center gap-2 justify-between">
        <span className="min-w-0 truncate text-sm font-medium text-white">
          {t('content-recording.select-events-to-publish-to')}
        </span>

        <button
          onClick={onClose}
          className={cx(
            'flex-shrink-0 p-2 hover:bg-gray-700 rounded-lg',
            'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
          )}
        >
          <span className="sr-only">{t('common.close')}</span>
          <CloseNewIcon className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
};

const Footer = ({
  onPublish,
  disabled,
  selectedEventsCount,
  selectedReplayAreasCount,
  ...rest
}: {
  onPublish: () => void;
  selectedEventsCount: number;
  selectedReplayAreasCount: number;
} & React.ComponentProps<'button'>) => {
  const { t } = useTranslation();
  const shouldShowSelectedReplayAreasCount = selectedEventsCount > 0;

  return (
    <footer
      className={cx('relative z-1 flex items-center gap-3 px-4 pt-5 pb-4', {
        'justify-between': shouldShowSelectedReplayAreasCount,
        'justify-end': !shouldShowSelectedReplayAreasCount,
      })}
    >
      {shouldShowSelectedReplayAreasCount && (
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-spring-neutral-b3">
          {t('content-recording.selected-replay-areas', {
            count: selectedReplayAreasCount,
          })}
        </span>
      )}

      <button
        className={cx(
          'flex-1 max-w-fit shrink-0 px-4 py-2 rounded-lg',
          'border',
          'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
          {
            'border-white/10': disabled,
            'bg-spring-primary border-transparent hover:bg-spring-primary/90':
              !disabled,
          },
        )}
        onClick={onPublish}
        disabled={disabled}
        {...rest}
      >
        <span
          className={cx('text-sm font-medium select-none', {
            'text-spring-neutral-b4-muted cursor-not-allowed': disabled,
            'text-white': !disabled,
          })}
        >
          {t('content-recording.publish-to-replay')}
        </span>
      </button>
    </footer>
  );
};
